import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardCheck, Users, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function Attendance() {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [members, setMembers] = useState([]);
  const [present, setPresent] = useState({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [visitors, setVisitors] = useState(0);
  const [word, setWord] = useState("");
  const [prayer, setPrayer] = useState("");
  const [offering, setOffering] = useState("");
  const [records, setRecords] = useState([]);

  useEffect(() => {
    api.get("/groups", { params: { active_only: true } }).then((r) => {
      setGroups(r.data);
      if (r.data[0]) setGroupId(r.data[0].id);
    });
  }, []);

  const loadGroup = useCallback(() => {
    if (!groupId) return;
    api.get("/members", { params: { group_id: groupId } }).then((r) => {
      setMembers(r.data);
      setPresent(Object.fromEntries(r.data.map((m) => [m.id, false])));
    });
    api.get("/attendance", { params: { group_id: groupId } }).then((r) => setRecords(r.data));
  }, [groupId]);

  useEffect(() => { loadGroup(); }, [loadGroup]);

  const toggle = (id) => setPresent((p) => ({ ...p, [id]: !p[id] }));
  const presentCount = Object.values(present).filter(Boolean).length;

  const submit = async () => {
    if (!groupId) return toast.error("Selecione um grupo");
    const ids = Object.entries(present).filter(([, v]) => v).map(([k]) => k);
    try {
      await api.post("/attendance", {
        group_id: groupId, date, present_member_ids: ids,
        visitors_count: Number(visitors) || 0, word_summary: word,
        prayer_requests: prayer, offering: Number(offering) || 0,
      });
      toast.success("Frequência registrada!");
      setWord(""); setPrayer(""); setOffering(""); setVisitors(0);
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar");
    }
  };

  const group = groups.find((g) => g.id === groupId);

  return (
    <div className="space-y-8 animate-fade-up" data-testid="attendance-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Registro de Frequência</h1>
        <p className="text-muted-foreground mt-1">Check-in semanal de presença por grupo.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Grupo</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="mt-1.5" data-testid="attendance-group-select"><SelectValue placeholder="Selecionar grupo" /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data do encontro</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="attendance-date-picker" />
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="attendance-member-list">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Presença</h3>
              <Badge className="bg-primary/15 text-primary border-none">{presentCount}/{members.length} presentes</Badge>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {members.map((m) => (
                <label key={m.id} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${present[m.id] ? "border-primary bg-primary/5" : "border-border"}`}>
                  <Checkbox checked={!!present[m.id]} onCheckedChange={() => toggle(m.id)} data-testid={`attendance-member-checkbox-${m.id}`} />
                  <span className="text-sm">{m.name}</span>
                </label>
              ))}
              {members.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-4">Este grupo ainda não tem membros. Adicione em Grupos & Líderes.</p>}
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Visitantes</Label>
                <Input type="number" min={0} value={visitors} onChange={(e) => setVisitors(e.target.value)} className="mt-1.5" data-testid="attendance-visitors-input" />
              </div>
              <div>
                <Label>Oferta (R$)</Label>
                <Input type="number" min={0} step="0.01" value={offering} onChange={(e) => setOffering(e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Resumo da palavra / tema</Label>
              <Textarea value={word} onChange={(e) => setWord(e.target.value)} className="mt-1.5" data-testid="attendance-word-input" />
            </div>
            <div>
              <Label>Pedidos de oração</Label>
              <Textarea value={prayer} onChange={(e) => setPrayer(e.target.value)} className="mt-1.5" />
            </div>
            <Button onClick={submit} className="w-full" data-testid="attendance-submit-button">
              <ClipboardCheck className="h-4 w-4 mr-2" /> Registrar frequência
            </Button>
          </Card>
        </div>

        <Card className="p-6 h-fit" data-testid="attendance-summary-card">
          <h3 className="font-display text-lg font-semibold mb-4">Encontros recentes {group ? `· ${group.name}` : ""}</h3>
          <div className="space-y-3">
            {records.map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-primary" /> {new Date(r.date + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                  <Badge variant="secondary">{r.present_member_ids.length + r.visitors_count} presentes</Badge>
                </div>
                {r.word_summary && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{r.word_summary}</p>}
              </div>
            ))}
            {records.length === 0 && <p className="text-sm text-muted-foreground">Nenhum registro ainda.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
